import classnames from 'classnames/bind'
import copy from 'copy-to-clipboard'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import Spinner from '~/components/Abstracts/Spinner'
import { asText } from '~/components/Abstracts/Storyblok'
import ProductCard from '~/components/UI/ProductCard'
import SquaredCta from '~/components/UI/SquaredCta'

import { useStyle } from '~/providers/StyleProvider'

import useAlerts from '~/hooks/useAlerts'
import { useGetWishList } from '~/hooks/wishlist/useGetWishlist'

import getSharedWishlistPublicUrl from '~/utils/get-shared-wishlist-public-url'

import { CTA, GENERAL, SEARCH } from '~/data/dictionary'
import { WishlistPageData } from '~/data/wishlist-page-data/serializer'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type WishlistProps = WishlistPageData & {
  className?: string
}

const CARD_SIZES = [{ breakpoint: 'md', ratio: 1 / 4 }, { ratio: 1 }]

function Wishlist({
  className,
  title,
  emptyTitle,
  upsales,
  shareMessageBody,
  shareMessageHead,
}: WishlistProps) {
  const { data, isLoading } = useGetWishList()

  const t = useTranslate()
  const triggerAlert = useAlerts()

  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })

  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.Title40SangBleu,
    color: GlobalThemeColors.Black,
  })

  const subtitleStyle = useStyle({
    textPreset: GlobalTextPreset.Label12Grotesk,
    textStyling: GlobalTextStyling.UpperCase,
    color: GlobalThemeColors.Black,
  })

  const urlStyle = useStyle({
    textPreset: GlobalTextPreset.Title15SangBleu,
    textStyling: GlobalTextStyling.UpperCase,
    color: GlobalThemeColors.Black,
  })

  const empty = !isLoading && data?.products?.length === 0

  const sharedWishlistPublicUrl = getSharedWishlistPublicUrl(data?.wishlistLid)

  const emailObject = shareMessageHead
  const emailMessage = `${shareMessageBody} ${sharedWishlistPublicUrl}`

  return (
    <section className={cx(className, css.Wishlist)}>
      <div className={gridStyle}>
        <div className={css.head}>
          <h1 className={titleStyle}>{asText(empty ? emptyTitle : title)}</h1>
        </div>
        {sharedWishlistPublicUrl && (
          <div className={css.shareLinkWrapper}>
            <div className={cx(css.copyButtonWrapper, urlStyle)}>
              <p className={css.url}>{sharedWishlistPublicUrl}</p>
              <SquaredCta
                className={css.button}
                withBorder={false}
                withHover={false}
                onClick={() => {
                  copy(sharedWishlistPublicUrl)
                  triggerAlert('COPY_SUCCESS')
                }}>
                {t(CTA.COPY)}
              </SquaredCta>
            </div>

            <SquaredCta
              className={css.mailLink}
              withBorder={false}
              withHover={false}
              href={`mailto:?subject=${emailObject}&body=${emailMessage}`}>
              {t(GENERAL.EMAIL)}
            </SquaredCta>
          </div>
        )}
        <div className={css.grid}>
          {isLoading ? (
            <div className={css.spinnerWrapper}>
              <Spinner className={css.spinner} />
            </div>
          ) : (
            !empty &&
            data.products.map((product) => {
              return (
                <ProductCard
                  key={`wishlist_item_${product?.product?.id}`}
                  sizesFromBreakpoints={CARD_SIZES}
                  wishlistDetails={{
                    inList: product?.wishlistDetails?.inList,
                    variantId: product?.wishlistDetails?.variantId,
                    tags: product?.tags,
                  }}
                  isWishListCard={true}
                  {...product?.product}
                />
              )
            })
          )}
        </div>
      </div>
      {upsales &&
        upsales?.length > 0 &&
        !isLoading &&
        data?.products?.length > 0 && (
          <div className={cx(gridStyle, css.gridBody)}>
            <p className={cx(css.subtitle, subtitleStyle)}>
              {t(SEARCH.MOST_POPULAR)}
            </p>

            {upsales?.map((product, index) => (
              <ProductCard
                className={css.item}
                key={`upsale_${index}`}
                isWishListCard={true}
                {...product}
              />
            ))}
          </div>
        )}
    </section>
  )
}

Wishlist.defaultProps = {}

export default Wishlist
